import React, { useMemo, useState } from 'react';
import { Check, RotateCcw, Sparkles, Wand2, X } from 'lucide-react';
import apiService from '../../services/apiService';
import NumericInput from './NumericInput';
import SeasonalityControl from './SeasonalityControl';
import { DEFAULT_SEASONALITY, describeSeasonality } from './forecastSeasonality';
import { ADJUSTMENT_YEARS, YEAR_LEVER_DEFS } from './forecastAdjustments';
import ForecastImpactPreviewTable from './ForecastImpactPreviewTable';
import {
  FORECAST_LEVER_PRIMARY_FIELDS,
  buildForecastImpactDiff
} from './forecastImpact';

// Item E: in the Ask AI box the intra-year shape applies to balance-growth levers
// (loan/deposit) through the existing quarterly-growth presets on accept, so only
// the preset-compatible profiles are offered here.
const ASK_AI_SEASONALITY_PROFILES = ['auto', 'flat', 'front', 'back'];

const PURPLE = '#9C6ADE';

const describeMacroCaseUsed = (macroCaseUsed) => {
  if (!macroCaseUsed) return null;
  if (macroCaseUsed.source === 'fallback-default') return 'Macro: fallback defaults';
  if (macroCaseUsed.source === 'central-settings') {
    return `Macro: ${macroCaseUsed.case || 'Base'} · central settings`;
  }
  return `Macro: ${macroCaseUsed.case || 'Base'}`;
};

const EXAMPLES = [
  'Raise commercial deposit balances ~$120M over the plan; show what moves',
  'Cut other operating expense by $4M/yr starting 2027',
  'Add 1.5 pts of loan growth in 2027 and 2028'
];

const ForecastPromptBox = ({
  scope = {},
  hasSubmission = false,
  canEdit = true,
  disabled = false,
  currentLevers = {},
  currentAnnual = {},
  sensitivity = {}, // {leverId:{year:$MM per +1pt}} — real per-point sensitivity from the model
  simulate, // (leverMap {leverId:[5]}) => [{field,label,measure,byYear:{year:{old,new}}}]
  refine, // (leverId, vector, targetDeltaMM, refYear) => refined vector (deterministic solve)
  onApply, // async (changeset) => void  (applies via setters, persists, records adjust event)
  onUndo, // async () => void
  onProposalMetadata
}) => {
  const [prompt, setPrompt] = useState('');
  const [seasonality, setSeasonality] = useState(DEFAULT_SEASONALITY); // Item E: intra-year shape
  const [state, setState] = useState('idle'); // idle | loading | ready | applied | error
  const [proposal, setProposal] = useState(null);
  const [editLevers, setEditLevers] = useState(null); // {leverId:[5 numbers]} editable copy
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  const readOnly = disabled || (hasSubmission && !canEdit);

  const draft = async () => {
    if (!prompt.trim() || state === 'loading' || readOnly) return;
    setState('loading');
    setError(null);
    setProposal(null);
    onProposalMetadata?.(null);
    try {
      const data = await apiService.fetchPromptChangeset({
        cycleCode: scope.cycleCode,
        entity: scope.planningEntity,
        division: scope.division,
        market: scope.market,
        prompt: prompt.trim(),
        macroCase: scope.macroCase || scope.macroMode || 'Base',
        seasonality,
        modelContext: { currentLevers, currentAnnual, leverSensitivity: sensitivity }
      });
      // For a dollar-target lever, refine the proposed points against the real model
      // so the simulated balance delta hits the stated target exactly before display.
      const nextEdit = {};
      (data.leverChanges || []).forEach((change) => {
        let vec = ADJUSTMENT_YEARS.map((year) => Number(change.newByYear?.[String(year)]) || 0);
        if (refine && change.anchor && change.anchor.targetDeltaMM != null && change.anchor.refYear) {
          vec = refine(change.leverId, vec, Number(change.anchor.targetDeltaMM), Number(change.anchor.refYear));
          change.newByYear = Object.fromEntries(ADJUSTMENT_YEARS.map((year, i) => [String(year), vec[i]]));
        }
        nextEdit[change.leverId] = vec;
      });
      setProposal(data);
      onProposalMetadata?.(data);
      setEditLevers(nextEdit);
      setState('ready');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not draft the change.');
      onProposalMetadata?.(null);
      setState('error');
    }
  };

  // Driven-line model fields + their driver label, from the proposal's driverLinks.
  const drivenByField = useMemo(() => {
    const map = {};
    (proposal?.leverChanges || []).forEach((change) => {
      (change.driverLinks || []).forEach((link) => {
        if (link.toModelField) {
          map[link.toModelField] = { driverName: link.driverName || link.toLine, why: link.why };
        }
      });
    });
    return map;
  }, [proposal]);

  const primaryFields = useMemo(
    () => new Set((proposal?.leverChanges || []).map((c) => FORECAST_LEVER_PRIMARY_FIELDS[c.leverId]).filter(Boolean)),
    [proposal]
  );

  // Recompute the dollar diff from the (editable) lever map via the parent's
  // deterministic single model object whenever the proposed values change.
  const diffLines = useMemo(() => {
    if (!editLevers || !simulate) return [];
    return buildForecastImpactDiff({
      simulate,
      leverMap: editLevers,
      years: ADJUSTMENT_YEARS
    });
  }, [editLevers, simulate]);

  const classify = (field) => {
    if (primaryFields.has(field)) return 'primary';
    if (drivenByField[field]) return 'driven';
    return 'flow';
  };

  const editLever = (leverId, yearIndex, value) => {
    setEditLevers((previous) => ({
      ...previous,
      [leverId]: (previous[leverId] || ADJUSTMENT_YEARS.map(() => 0)).map((v, i) =>
        (i === yearIndex ? Number(value) || 0 : v)
      )
    }));
  };

  const reset = () => {
    setProposal(null);
    setEditLevers(null);
    setState('idle');
    setError(null);
    onProposalMetadata?.(null);
  };

  const accept = async () => {
    if (!proposal || !editLevers || applying || readOnly) return;
    setApplying(true);
    try {
      const changeset = {
        prompt: prompt.trim(),
        interpretation: proposal.interpretation,
        summary: proposal.summary,
        leverChanges: proposal.leverChanges.map((change) => ({
          ...change,
          newByYear: Object.fromEntries(
            ADJUSTMENT_YEARS.map((year, index) => [String(year), editLevers[change.leverId]?.[index] ?? 0])
          )
        })),
        secondOrder: diffLines
          .filter((line) => classify(line.field) === 'driven')
          .map((line) => ({
            field: line.field,
            label: line.label,
            driver: drivenByField[line.field]?.driverName,
            totalDeltaMM: Number(line.totalDelta.toFixed(2))
          })),
        citedContextNotes: proposal.contextNotes?.cited || [],
        seasonality, // Item E: applied to balance-growth levers via quarterly presets
        generator: proposal.generator
      };
      await onApply(changeset);
      setState('applied');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not apply the change.');
      setState('error');
    } finally {
      setApplying(false);
    }
  };

  const undo = async () => {
    if (!onUndo || applying) return;
    setApplying(true);
    try {
      await onUndo();
      reset();
    } finally {
      setApplying(false);
    }
  };

  const citedRefs = (proposal?.contextNotes?.loaded || []).filter((ref) =>
    (proposal?.contextNotes?.cited || []).includes(ref.ref)
  );
  const macroCaseLabel = describeMacroCaseUsed(proposal?.macroCaseUsed);

  return (
    <div className="forecast-prompt-box">
      <div className="forecast-panel-head">
        <div>
          <span className="forecast-section-code">OP_03P · AI prompt box</span>
          <h3>
            <Wand2 size={15} style={{ marginRight: 6, verticalAlign: '-2px', color: PURPLE }} />
            Describe a change in plain English — review the second-order effects before it touches the model
          </h3>
        </div>
      </div>

      {readOnly && (
        <p className="forecast-submit-note">
          This package is read-only for your role or current status, so AI-drafted changes are disabled.
        </p>
      )}
      {!readOnly && !hasSubmission && (
        <p className="forecast-submit-note">
          Open a market package (an entity session) to apply AI-drafted changes; here you can still preview wording.
        </p>
      )}

      <div className="forecast-prompt-input">
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="e.g. Raise commercial deposit balances by about $120M across the plan and show what moves."
          rows={2}
          disabled={readOnly}
          aria-label="Describe the forecast change"
        />
        <button
          type="button"
          className="forecast-primary-button compact"
          onClick={draft}
          disabled={readOnly || !prompt.trim() || state === 'loading'}
        >
          <Wand2 size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
          {state === 'loading' ? 'Drafting…' : 'Draft change'}
        </button>
      </div>

      <SeasonalityControl
        value={seasonality}
        onChange={setSeasonality}
        options={ASK_AI_SEASONALITY_PROFILES}
        showIntraQuarter={false}
        disabled={readOnly}
        note="Shapes how a loan/deposit growth change is distributed across the year (annual target preserved). $MM fee/expense changes post per the model."
      />

      {state === 'idle' && (
        <div className="forecast-prompt-examples">
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              className="forecast-health-chip clickable neutral"
              onClick={() => setPrompt(example)}
              disabled={readOnly}
            >
              {example}
            </button>
          ))}
        </div>
      )}

      {state === 'loading' && (
        <p className="forecast-submit-note">
          <Sparkles size={12} style={{ marginRight: 5, verticalAlign: '-2px' }} />
          Reading your context notes, then sizing the change with Sonnet against the real model and
          driver-linkage map… (~15–35s)
        </p>
      )}

      {state === 'error' && <p className="forecast-submit-note forecast-prompt-error">{error}</p>}

      {(state === 'ready' || state === 'applied') && proposal && (
        <div className="ai-draft-panel forecast-prompt-proposal">
          <div className="forecast-submit-chips">
            <span
              className="forecast-health-chip"
              style={{ borderColor: PURPLE, background: 'color-mix(in srgb, #9C6ADE 14%, transparent)' }}
            >
              <Sparkles size={11} style={{ marginRight: 4 }} />
              AI-drafted · {proposal.generator || 'agent'}
            </span>
            <span className="forecast-health-chip neutral" title="Intra-year shape applied to loan/deposit growth changes">
              shape: {describeSeasonality(seasonality)}
            </span>
            {macroCaseLabel && <span className="forecast-health-chip neutral">{macroCaseLabel}</span>}
            {state === 'applied' && <span className="forecast-health-chip green">applied to OP_03 levers</span>}
          </div>

          {proposal.interpretation && (
            <p className="ai-draft-method"><strong>{proposal.interpretation}</strong></p>
          )}
          {proposal.summary && <p className="forecast-submit-note">{proposal.summary}</p>}
          {proposal.assumptions && (
            <p className="forecast-submit-note"><em>Assumption: {proposal.assumptions}</em></p>
          )}

          {/* Proposed lever changes — editable before accepting */}
          <div className="forecast-prompt-levers">
            <div className="forecast-prompt-subhead">Proposed lever changes {state === 'ready' && !readOnly ? '(editable)' : ''}</div>
            <p className="forecast-basis-note">
              These are the AI's proposed lever settings — edit any value before accepting. Every dollar in the
              effect table below is computed by the model from these levers, not by the AI.
            </p>
            {(proposal.leverChanges || []).map((change) => {
              const def = YEAR_LEVER_DEFS.find((d) => d.id === change.leverId) || {};
              return (
                <div key={change.leverId} className="forecast-prompt-lever-row">
                  <div className="forecast-prompt-lever-name">
                    <strong>{change.label}</strong>
                    <small>{change.glGroup} · {change.unit}</small>
                    {change.anchor && change.anchor.targetDeltaMM != null && (
                      <small className="forecast-prompt-anchor">
                        anchored to {change.anchor.targetDeltaMM > 0 ? '+' : ''}${Math.abs(change.anchor.targetDeltaMM).toFixed(0)}M at FY{change.anchor.refYear}
                      </small>
                    )}
                    {change.rationale && <small className="forecast-prompt-rationale">{change.rationale}</small>}
                  </div>
                  <div className="forecast-prompt-lever-years">
                    {ADJUSTMENT_YEARS.map((year, index) => {
                      const oldVal = Number(change.oldByYear?.[String(year)]) || 0;
                      const newVal = editLevers?.[change.leverId]?.[index] ?? 0;
                      return (
                        <div key={year} className="forecast-prompt-year">
                          <span className="forecast-prompt-year-label">FY{year}</span>
                          {state === 'ready' && !readOnly ? (
                            <NumericInput
                              value={newVal}
                              min={def.min}
                              max={def.max}
                              step={def.step || 0.25}
                              onCommit={(value) => editLever(change.leverId, index, value)}
                            />
                          ) : (
                            <span className="forecast-prompt-year-value">{newVal}</span>
                          )}
                          <span className="forecast-prompt-year-old" title="current value">
                            was {oldVal}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Second-order effects from the deterministic single model object */}
          <ForecastImpactPreviewTable
            diffLines={diffLines}
            years={ADJUSTMENT_YEARS}
            classifyLine={(line) => classify(line.field)}
            drivenByField={drivenByField}
          />

          {/* Cited Item B context notes */}
          {proposal.contextNotes?.count > 0 && (
            <div className="forecast-submit-chips ai-context-cites">
              <span
                className="forecast-health-chip"
                style={{ borderColor: PURPLE, background: 'color-mix(in srgb, #9C6ADE 12%, transparent)' }}
              >
                <Sparkles size={11} style={{ marginRight: 4 }} />
                read {proposal.contextNotes.count} context note{proposal.contextNotes.count === 1 ? '' : 's'}
              </span>
              {citedRefs.map((ref) => (
                <span className="forecast-health-chip moss" key={ref.ref} title={ref.excerpt}>
                  cited {ref.ref}: {ref.scope}
                </span>
              ))}
              {(proposal.contextNotes.cited || []).length === 0 && (
                <span className="forecast-health-chip neutral">none cited as decisive</span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="forecast-prompt-actions">
            {state === 'ready' && (
              <>
                <button
                  type="button"
                  className="forecast-primary-button compact"
                  onClick={accept}
                  disabled={readOnly || applying || !hasSubmission || diffLines.length === 0}
                  title={!hasSubmission ? 'Open a market package to apply changes' : undefined}
                >
                  <Check size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                  {applying ? 'Applying…' : 'Accept & apply'}
                </button>
                <button type="button" className="forecast-secondary-button compact" onClick={reset} disabled={applying}>
                  <X size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                  Reject
                </button>
              </>
            )}
            {state === 'applied' && (
              <button type="button" className="forecast-secondary-button compact" onClick={undo} disabled={applying}>
                <RotateCcw size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                {applying ? 'Undoing…' : 'Undo this change'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastPromptBox;
